
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Settings, Save, Plus } from "lucide-react";

interface AppSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_type: string;
  description?: string;
  category?: string;
  created_at: string;
  updated_at: string;
}

const AppSettingsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingSettings, setEditingSettings] = useState<Record<string, string>>({});

  // Fetch all app settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .order('category', { ascending: true })
        .order('setting_key', { ascending: true });
      
      if (error) throw error;
      return data as AppSetting[];
    },
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: async ({ id, setting_value }: { id: string; setting_value: string }) => {
      const { error } = await supabase
        .from('app_settings')
        .update({ 
          setting_value,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      toast({
        title: "Success",
        description: "Setting updated successfully.",
      });
      setEditingSettings({});
    },
    onError: (error) => {
      console.error('Error updating setting:', error);
      toast({
        title: "Error",
        description: "Failed to update setting. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleUpdateSetting = (setting: AppSetting) => {
    const newValue = editingSettings[setting.id];
    if (newValue !== undefined && newValue !== setting.setting_value) {
      updateSettingMutation.mutate({
        id: setting.id,
        setting_value: newValue
      });
    } else {
      // Cancel editing if no changes
      setEditingSettings(prev => {
        const { [setting.id]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleInputChange = (settingId: string, value: string) => {
    setEditingSettings(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  const isEditing = (settingId: string) => settingId in editingSettings;
  const getCurrentValue = (setting: AppSetting) => 
    isEditing(setting.id) ? editingSettings[setting.id] : setting.setting_value;

  const groupedSettings = settings?.reduce((acc, setting) => {
    const category = setting.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(setting);
    return acc;
  }, {} as Record<string, AppSetting[]>) || {};

  const renderSettingInput = (setting: AppSetting) => {
    const currentValue = getCurrentValue(setting);
    const editing = isEditing(setting.id);

    switch (setting.setting_type) {
      case 'number':
        return (
          <Input
            type="number"
            value={currentValue}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-gray-50" : ""}
          />
        );
      case 'boolean':
        return (
          <Select
            value={currentValue}
            onValueChange={(value) => handleInputChange(setting.id, value)}
            disabled={!editing}
          >
            <SelectTrigger className={!editing ? "bg-gray-50" : ""}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'json':
        return (
          <Textarea
            value={currentValue}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-gray-50" : ""}
            rows={3}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={currentValue}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
            disabled={!editing}
            className={!editing ? "bg-gray-50" : ""}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="h-6 w-6" />
        <h2 className="text-2xl font-bold">App Settings</h2>
      </div>

      {Object.entries(groupedSettings).map(([category, categorySettings]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="capitalize">
              {category.replace('_', ' ')} Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {categorySettings.map((setting) => (
              <div key={setting.id} className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 border rounded-lg">
                <div className="space-y-2">
                  <Label className="font-semibold">{setting.setting_key}</Label>
                  {setting.description && (
                    <p className="text-sm text-gray-600">{setting.description}</p>
                  )}
                  <div className="text-xs text-gray-500">
                    Type: {setting.setting_type}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Value</Label>
                  {renderSettingInput(setting)}
                </div>

                <div className="flex items-end gap-2">
                  {isEditing(setting.id) ? (
                    <>
                      <Button
                        onClick={() => handleUpdateSetting(setting)}
                        disabled={updateSettingMutation.isPending}
                        size="sm"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEditingSettings(prev => {
                          const { [setting.id]: _, ...rest } = prev;
                          return rest;
                        })}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setEditingSettings(prev => ({
                        ...prev,
                        [setting.id]: setting.setting_value
                      }))}
                      size="sm"
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {Object.keys(groupedSettings).length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Settings Found</h3>
            <p className="text-gray-600">No application settings have been configured yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AppSettingsManagement;
